import React, { useState } from 'react';
import { Product } from '../data/products';
import { useLanguage } from '../context/LanguageContext';
import { productTranslations } from '../data/translations';
import { useAppMode } from '../context/AppModeContext';

interface ProductCardProps {
  product: Product;
  priority?: 'high' | 'low'; // Loading priority for the product image
}

// Utility function to convert full FELCO URLs to relative paths
const convertToRelativePath = (fullUrl: string): string => {
  try {
    const url = new URL(fullUrl);
    // Extract the path part (e.g., "/products/felco-2" from "https://www.felco.com/products/felco-2")
    return url.pathname;
  } catch (error) {
    // If URL parsing fails, return the original URL
    console.warn('Failed to parse URL for relative conversion:', fullUrl);
    return fullUrl;
  }
};

// Function to get the parent window's origin when in iframe
const getParentOrigin = (): string | null => {
  try {
    // Try to access parent window's origin
    if (window.parent && window.parent !== window.self) {
      return window.parent.location.origin;
    }
  } catch (error) {
    // Cross-origin restrictions prevent direct access
    // Try to get it from document.referrer as fallback
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        return referrerUrl.origin;
      } catch (e) {
        console.warn('Could not parse referrer URL:', document.referrer);
      }
    }
  }
  return null;
};

// Function to extract language code from parent URL
const getParentLanguageCode = (): string | null => {
  const checkPathForLanguageCode = (path: string): string | null => {
    const pathSegments = path.split('/').filter(segment => segment);
    // Language codes are typically the first segment after domain
    // Examples: /de/pages/... or /fr-fr/pages/...
    if (pathSegments.length > 0) {
      const firstSegment = pathSegments[0];
      // Check if it looks like a language code (2-5 characters, may contain hyphen)
      if (firstSegment.match(/^[a-z]{2}(-[a-z]{2})?$/i)) {
        return firstSegment;
      }
    }
    return null;
  };

  try {
    // First try to get language code from current window location
    const currentPath = window.location.pathname;
    const currentLanguageCode = checkPathForLanguageCode(currentPath);
    if (currentLanguageCode) {
      return currentLanguageCode;
    }

    // Then try to get parent URL directly
    if (window.parent && window.parent !== window.self) {
      const parentUrl = window.parent.location.pathname;
      const parentLanguageCode = checkPathForLanguageCode(parentUrl);
      if (parentLanguageCode) {
        return parentLanguageCode;
      }
    }
  } catch (error) {
    // Cross-origin fallback using document.referrer
    if (document.referrer) {
      try {
        const referrerUrl = new URL(document.referrer);
        return checkPathForLanguageCode(referrerUrl.pathname);
      } catch (e) {
        console.warn('Could not parse referrer URL for language code:', document.referrer);
      }
    }
  }
  return null;
};

const ProductCard: React.FC<ProductCardProps> = ({ product, priority = 'low' }) => {
  const { language, t } = useLanguage();
  const { showWebsiteLinks, useRelativeUrls } = useAppMode();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Get translated content or fall back to English content
  const getLocalizedDescription = () => {
    if (language === 'en') {
      return product.description;
    }
    return productTranslations[product.id]?.[language]?.description || product.description;
  };

  const getLocalizedSpecs = () => {
    if (language === 'en') {
      return product.shortSpecs;
    }
    return productTranslations[product.id]?.[language]?.shortSpecs || product.shortSpecs;
  };

  // Get product URL with proper formatting based on mode
  const getProductUrl = () => {
    let baseUrl = product.productUrl;
    
    // Handle iframe mode with parent domain detection
    if (useRelativeUrls) {
      const parentOrigin = getParentOrigin();
      const parentLanguageCode = getParentLanguageCode();
      let relativePath = convertToRelativePath(baseUrl);
      
      // Debug logging
      console.log('URL Construction Debug:', {
        originalUrl: product.productUrl,
        parentOrigin,
        parentLanguageCode,
        relativePath,
        useRelativeUrls,
        referrer: document.referrer
      });
      
      if (parentOrigin) {
        // Include language code in the path if detected
        if (parentLanguageCode) {
          relativePath = `/${parentLanguageCode}${relativePath}`;
        }
        // Use parent window's domain + language-aware relative path
        baseUrl = `${parentOrigin}${relativePath}`;
        
        console.log('Final constructed URL:', baseUrl);
      } else {
        // Fallback to relative path (original behavior)
        baseUrl = relativePath;
        console.log('Fallback to relative path:', baseUrl);
      }
    }
    
    // Add UTM parameters only for full URLs (not relative ones)
    if (!useRelativeUrls || getParentOrigin()) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      baseUrl = `${baseUrl}${separator}utm_source=product_selector&utm_medium=${language}&utm_campaign=selector`;
    }
    
    return baseUrl;
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-all transform hover:-translate-y-1 border border-gray-100">
      <div className="w-full h-64 overflow-hidden relative">
        {/* Loading placeholder */}
        {!imageLoaded && !imageError && (
          <div className="absolute inset-0 bg-gray-100 animate-pulse flex items-center justify-center">
            <div className="text-gray-400 text-sm">Loading...</div>
          </div>
        )}
        
        {/* Main product image */}
        <img 
          src={product.imageUrl} 
          alt={product.model} 
          className={`w-full h-full object-contain p-4 transition-opacity duration-300 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading={priority === 'high' ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={priority}
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            e.currentTarget.onerror = null;
            setImageError(true);
            e.currentTarget.src = "https://via.placeholder.com/300x300/ffffff/E30613?text=FELCO+" + encodeURIComponent(product.model);
          }}
        />
        
        {/* Error fallback */}
        {imageError && (
          <div className="absolute inset-0 bg-red-50 flex items-center justify-center p-4">
            <div className="text-center text-red-600">
              <div className="text-2xl font-bold mb-2">FELCO</div>
              <div className="text-sm">{product.model}</div>
            </div>
          </div>
        )}
      </div>
      <div className="p-6">
        <h3 className="text-2xl font-bold text-felco-red mb-3">{product.model}</h3>
        <p className="text-gray-700 mb-5 text-lg">{getLocalizedDescription()}</p>
        
        <div className="mb-6">
          <h4 className="font-medium text-gray-800 mb-3 text-lg">{t('results.features')}</h4>
          <ul className="space-y-2">
            {getLocalizedSpecs().map((spec, index) => (
              <li key={index} className="text-gray-600 flex items-start">
                <svg className="w-5 h-5 text-felco-red mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"></path>
                </svg>
                <span>{spec}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {showWebsiteLinks && (
          <a 
            href={getProductUrl()} 
            target={useRelativeUrls ? '_parent' : '_blank'}
            rel="noopener noreferrer" 
            className="block w-full bg-felco-red hover:bg-felco-dark-red text-white font-medium py-3 px-4 rounded-lg text-center transition-all hover:shadow-md"
          >
            {t('btn.viewProduct')}
          </a>
        )}
      </div>
    </div>
  );
};

export default ProductCard; 